import { useCallback, useEffect, useRef, useState } from "react";

import Config from "~/config";
import logger from "~/helpers/logger";
import pullSavesInFlight from "~/helpers/offline/pullSavesInFlight";
import { OFFLINE_QUEUE_APP, RECORD_ORDER_PULLS_MUTATION, VERIFY_CONTAINER_MUTATION } from "~/helpers/offline/queueMeta";
import createDrainTriggerScheduler, { type DrainTriggerScheduler } from "~/helpers/sw/drainTriggerScheduler";
import isSwOutboundMessage, {
	QUEUE_FAILURE_REASON_KIND,
	type QueueReplayFailedMessage,
	type QueueReplaySuccessMessage,
	SW_MESSAGE_TYPE
} from "~/helpers/sw/messageContract";
import postToServiceWorker from "~/helpers/sw/postToServiceWorker";
import requestStoragePersistence, { persistenceScope } from "~/helpers/sw/storagePersistence";
import { useAppDispatch, useAppStore } from "~/hooks/reduxHooks";
import NetworkStatusContext from "~/providers/NetworkStatusContext";
import isStandaloneDisplay from "~/pwa/isStandaloneDisplay";
import { commitPendingPatch, discardPendingPatch } from "~/store/availabilitySlice";
import { commitReplayedPost, discardPendingPost, failPendingPost } from "~/store/engageSlice";
import { removeFloatingEntry } from "~/store/eventsTimerSlice";
import {
	bumpQueueSuccessSignal,
	clearAssignedCountInFlight,
	clearCountConfirmationWatch,
	confirmCountConfirmationWatch,
	removePendingShipByCreateActionKey
} from "~/store/materialMobileSlice";
import { removePendingWrite } from "~/store/materialPendingWritesSlice";
import {
	discardQueueEntry,
	enqueueMutation,
	markQueueReadFailed,
	markQueueReplayFailed,
	markQueueReplayStarted,
	markQueueReplaySuccess,
	markQueueUnavailable,
	markReplayAcknowledged,
	setQueueSnapshot
} from "~/store/offlineQueueSlice";
import { bumpReadCacheWriteSignal, bumpReadRefreshSignal, markReadCacheServed } from "~/store/readCacheSlice";
import { recordShrinkReplayEvent, type ShrinkReplayOperationKind } from "~/store/shrinkMobileSlice";
import { setStoragePersisted } from "~/store/storageDurabilitySlice";
import { setSyncActivity } from "~/store/syncActivitySlice";
import { setSyncMode } from "~/store/syncModeSlice";
import {
	clearPendingPull,
	markContainerSubmitLanded,
	markPendingPullFailed,
	prunePendingPulls
} from "~/store/verifySlice";

interface NetworkStatusProviderProps {
	children: React.ReactNode;
}

const REVIEW_QUEUE_SETTLING_MUTATIONS = new Set(["LinkFloatingTimeEntry", "DiscardFloatingTimeEntry"]);

const resolveSettledFloatingEntryId = (message: QueueReplaySuccessMessage): string | undefined => {
	const isEventsEntry = message.app === undefined || message.app === OFFLINE_QUEUE_APP.EVENTS;
	if (!isEventsEntry) return undefined;
	if (!REVIEW_QUEUE_SETTLING_MUTATIONS.has(message.mutationName)) return undefined;
	return message.reconciliationId ?? message.floatingTimeEntryId;
};

const isAvailabilityEntry = (app: QueueReplaySuccessMessage["app"]): boolean => app === OFFLINE_QUEUE_APP.AVAILABILITY;

const isEngageEntry = (app: QueueReplaySuccessMessage["app"]): boolean => app === OFFLINE_QUEUE_APP.ENGAGE;

const isMaterialEntry = (app: QueueReplaySuccessMessage["app"]): boolean => app === OFFLINE_QUEUE_APP.MATERIAL;

const isShrinkEntry = (app: QueueReplaySuccessMessage["app"]): boolean => app === OFFLINE_QUEUE_APP.SHRINK;

const shrinkReplayKind = (mutationName: string): ShrinkReplayOperationKind | undefined => {
	if (mutationName === "RecordShrinkEvent") return "record";
	if (mutationName === "RemoveShrinkEvent") return "remove";
	return undefined;
};

const isVerifyEntry = (app: QueueReplaySuccessMessage["app"]): boolean => app === OFFLINE_QUEUE_APP.VERIFY;

const pendingPullKeyOf = (message: Pick<QueueReplaySuccessMessage, "app" | "mutationName" | "reconciliationId">) =>
	isVerifyEntry(message.app) && message.mutationName === RECORD_ORDER_PULLS_MUTATION
		? message.reconciliationId
		: undefined;

const livePendingPullKeys = (
	entries: readonly Pick<QueueReplaySuccessMessage, "app" | "mutationName" | "reconciliationId">[]
) => entries.map((entry) => pendingPullKeyOf(entry)).filter((key): key is string => key !== undefined);

const COUNT_MATERIAL_ITEM_MUTATION = "CountMaterialItem";

const isTerminalReplayFailure = (message: QueueReplayFailedMessage): boolean =>
	message.reasonKind === QUEUE_FAILURE_REASON_KIND.BUSINESS && message.retryable !== true;

const ONLINE_INTERVAL = 30_000;
const OFFLINE_INTERVAL = 5_000;
const FAILURE_THRESHOLD = 2;
// navigator.serviceWorker.ready never rejects — it stays pending forever when registration never completes,
// so the handshake needs its own bound before consumers can conclude the queue's state is settled.
const QUEUE_STATE_HANDSHAKE_TIMEOUT = 5_000;
// A silent registration gets re-probed this many times before the switch is left refused rather than allowed.
const QUEUE_STATE_HANDSHAKE_MAX_ATTEMPTS = 3;

const NetworkStatusProvider = ({ children }: NetworkStatusProviderProps) => {
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const wasOnline = useRef(navigator.onLine);
	const failureCount = useRef(0);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const drainSchedulerRef = useRef<DrainTriggerScheduler | null>(null);
	const dispatch = useAppDispatch();
	const store = useAppStore();
	// Mirrors snapshot arrival for the handshake timer WITHOUT putting isSnapshotLoaded in the SW effect's
	// deps — that would tear down and re-register the `message` listener on every snapshot.
	const snapshotArrivedRef = useRef(false);

	const getDrainScheduler = useCallback((): DrainTriggerScheduler => {
		if (!drainSchedulerRef.current) {
			drainSchedulerRef.current = createDrainTriggerScheduler(() => {
				if (!navigator.onLine) return;
				postToServiceWorker({ type: SW_MESSAGE_TYPE.DRAIN_QUEUE_NOW });
				dispatch(bumpReadRefreshSignal());
			});
		}
		return drainSchedulerRef.current;
	}, []);

	const checkConnectivity = useCallback(async () => {
		try {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 5_000);
			const baseUrl = new URL(Config.endpoint).origin;
			await fetch(`${baseUrl}/healthz`, {
				method: "GET",
				signal: controller.signal
			});
			clearTimeout(timeout);
			failureCount.current = 0;
			setIsOnline(true);
		} catch {
			failureCount.current += 1;
			if (failureCount.current >= FAILURE_THRESHOLD) {
				setIsOnline(false);
			}
		}
	}, []);

	useEffect(() => {
		const handleOnline = () => {
			failureCount.current = 0;
			setIsOnline(true);
			getDrainScheduler().trigger();
		};
		const handleOffline = () => {
			setIsOnline(false);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, [getDrainScheduler]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") getDrainScheduler().trigger();
		};
		const handlePageShow = () => {
			getDrainScheduler().trigger();
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("pageshow", handlePageShow);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("pageshow", handlePageShow);
		};
	}, [getDrainScheduler]);

	useEffect(() => {
		if (isOnline && !wasOnline.current) getDrainScheduler().trigger();
		wasOnline.current = isOnline;
	}, [isOnline, getDrainScheduler]);

	useEffect(() => {
		requestStoragePersistence(navigator.storage, window.localStorage, persistenceScope(isStandaloneDisplay()))
			.then(({ persisted }) => dispatch(setStoragePersisted(persisted)))
			.catch((err) => logger.error("[storage] persistence probe failed", err));
	}, []);

	useEffect(() => {
		if (intervalRef.current) clearInterval(intervalRef.current);
		const interval = isOnline ? ONLINE_INTERVAL : OFFLINE_INTERVAL;
		intervalRef.current = setInterval(checkConnectivity, interval);
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [isOnline, checkConnectivity]);

	useEffect(() => {
		if (!("serviceWorker" in navigator)) {
			// No SW means no fetch interception, so no mutation can ever be queued — the state is known, and empty.
			dispatch(markQueueUnavailable());
			return;
		}
		const dropPendingShipWithItsWrite = (createActionKey: string) => {
			for (const pendingShip of Object.values(store.getState().materialMobile.pendingShips)) {
				if (pendingShip.createActionKey === createActionKey) dispatch(removePendingWrite(pendingShip.shipActionKey));
			}
			dispatch(removePendingShipByCreateActionKey(createActionKey));
		};
		const handleMessage = (event: MessageEvent) => {
			if (!isSwOutboundMessage(event.data)) return;
			const message = event.data;
			switch (message.type) {
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED:
					dispatch(markQueueReplayStarted());
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE:
					dispatch(markReplayAcknowledged());
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED:
					dispatch(enqueueMutation(message));
					return;
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS: {
					dispatch(markQueueReplaySuccess(message));
					const settledFloatingEntryId = resolveSettledFloatingEntryId(message);
					if (settledFloatingEntryId !== undefined) {
						dispatch(removeFloatingEntry(settledFloatingEntryId));
					}
					if (isAvailabilityEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(commitPendingPatch(message.reconciliationId));
					}
					if (isEngageEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(commitReplayedPost(message.reconciliationId));
					}
					if (isMaterialEntry(message.app)) {
						dispatch(bumpQueueSuccessSignal(message.mutationName));
						if (message.reconciliationId !== undefined) {
							dispatch(clearAssignedCountInFlight(message.reconciliationId));
							if (message.mutationName === COUNT_MATERIAL_ITEM_MUTATION) {
								dispatch(confirmCountConfirmationWatch(message.reconciliationId));
							}
						}
					}
					if (isShrinkEntry(message.app)) {
						const kind = shrinkReplayKind(message.mutationName);
						if (kind)
							dispatch(recordShrinkReplayEvent({ kind, outcome: "success", shrinkEventId: message.reconciliationId }));
					}
					if (
						isVerifyEntry(message.app) &&
						message.reconciliationId !== undefined &&
						message.mutationName === VERIFY_CONTAINER_MUTATION
					) {
						dispatch(markContainerSubmitLanded(message.reconciliationId));
					}
					const landedPullKey = pendingPullKeyOf(message);
					if (landedPullKey !== undefined) dispatch(clearPendingPull(landedPullKey));
					return;
				}
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED: {
					dispatch(markQueueReplayFailed(message));
					if (
						isAvailabilityEntry(message.app) &&
						isTerminalReplayFailure(message) &&
						message.reconciliationId !== undefined
					) {
						dispatch(discardPendingPatch(message.reconciliationId));
					}
					if (
						isEngageEntry(message.app) &&
						isTerminalReplayFailure(message) &&
						message.reconciliationId !== undefined
					) {
						dispatch(failPendingPost({ idempotencyKey: message.reconciliationId, refusal: message.refusal ?? null }));
					}
					if (isMaterialEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(clearAssignedCountInFlight(message.reconciliationId));
						if (isTerminalReplayFailure(message)) {
							dropPendingShipWithItsWrite(message.reconciliationId);
							if (message.mutationName === COUNT_MATERIAL_ITEM_MUTATION) {
								dispatch(clearCountConfirmationWatch(message.reconciliationId));
							}
						}
					}
					if (isShrinkEntry(message.app) && isTerminalReplayFailure(message)) {
						const kind = shrinkReplayKind(message.mutationName);
						if (kind)
							dispatch(recordShrinkReplayEvent({ kind, outcome: "failed", shrinkEventId: message.reconciliationId }));
					}
					const refusedPullKey = pendingPullKeyOf(message);
					if (refusedPullKey !== undefined && isTerminalReplayFailure(message)) {
						dispatch(markPendingPullFailed(refusedPullKey));
					}
					return;
				}
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_DISCARDED: {
					dispatch(discardQueueEntry(message.clientId));
					if (isAvailabilityEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(discardPendingPatch(message.reconciliationId));
					}
					if (isEngageEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(discardPendingPost(message.reconciliationId));
					}
					if (isMaterialEntry(message.app) && message.reconciliationId !== undefined) {
						dispatch(clearAssignedCountInFlight(message.reconciliationId));
						dropPendingShipWithItsWrite(message.reconciliationId);
						dispatch(removePendingWrite(message.reconciliationId));
						if (message.mutationName === COUNT_MATERIAL_ITEM_MUTATION) {
							dispatch(clearCountConfirmationWatch(message.reconciliationId));
						}
					}
					const discardedPullKey = pendingPullKeyOf(message);
					if (discardedPullKey !== undefined) dispatch(clearPendingPull(discardedPullKey));
					return;
				}
				case SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT:
					snapshotArrivedRef.current = true;
					dispatch(setQueueSnapshot(message.entries));
					dispatch(
						prunePendingPulls({
							inFlightKeys: pullSavesInFlight(),
							liveKeys: livePendingPullKeys(message.entries),
							now: Date.now()
						})
					);
					return;
				case SW_MESSAGE_TYPE.QUEUE_SNAPSHOT_UNAVAILABLE:
					dispatch(markQueueReadFailed());
					return;
				case SW_MESSAGE_TYPE.READ_CACHE_SERVED:
					dispatch(markReadCacheServed(message));
					return;
				case SW_MESSAGE_TYPE.READ_CACHE_UPDATED:
					dispatch(bumpReadCacheWriteSignal());
					return;
				case SW_MESSAGE_TYPE.SYNC_ACTIVITY:
					dispatch(setSyncActivity(message.records));
					return;
				case SW_MESSAGE_TYPE.SYNC_MODE:
					dispatch(setSyncMode({ mode: message.mode, ...(message.reason ? { reason: message.reason } : {}) }));
					return;
			}
		};
		navigator.serviceWorker.addEventListener("message", handleMessage);

		let cancelled = false;
		let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

		// Bounded fallback: covers a ready promise that never settles. A missing registration proves the queue
		// cannot exist, but an existing one that has not answered proves nothing, so it gets re-probed first.
		const armHandshakeTimeout = (attemptsRemaining: number): void => {
			handshakeTimer = setTimeout(() => {
				// The snapshot already answered, so the state is known and the retry has nothing left to cover.
				// Re-probing would make the SW re-broadcast a full snapshot, and setQueueSnapshot rebuilds byApp
				// from the worker's queue alone — dropping terminal business failures (which the worker no longer
				// holds) and nulling the reason on every surviving entry.
				if (snapshotArrivedRef.current) return;
				void navigator.serviceWorker.getRegistration().then(
					(registration) => {
						if (cancelled) return;
						if (!registration) {
							dispatch(markQueueUnavailable());
							return;
						}
						if (attemptsRemaining <= 1) return;
						registration.active?.postMessage({ type: SW_MESSAGE_TYPE.REQUEST_QUEUE_STATE });
						armHandshakeTimeout(attemptsRemaining - 1);
					},
					() => {
						// getRegistration() rejects with SecurityError where storage access is denied. Unhandled, the
						// state would stay unknown all session and every tenant switch would be refused. Attached as a
						// rejection handler on getRegistration() rather than a chained .catch, so a postMessage failure
						// in the branch above can never be misread as "no registration".
						if (cancelled) return;
						dispatch(markQueueUnavailable());
					}
				);
			}, QUEUE_STATE_HANDSHAKE_TIMEOUT);
		};
		armHandshakeTimeout(QUEUE_STATE_HANDSHAKE_MAX_ATTEMPTS);

		// Mount-time handshake: ask the SW for any in-flight queue entries so the slice
		// reconciles on refresh (slice is NOT persisted — see store/index.ts whitelist note).
		navigator.serviceWorker.ready
			.then((registration) => {
				registration.active?.postMessage({ type: SW_MESSAGE_TYPE.REQUEST_QUEUE_STATE });
				registration.active?.postMessage({ type: SW_MESSAGE_TYPE.REQUEST_SYNC_MODE });
				registration.active?.postMessage({ type: SW_MESSAGE_TYPE.REQUEST_SYNC_ACTIVITY });
				getDrainScheduler().trigger();
			})
			.catch(() => {
				// SW not installed yet (e.g., dev without PWA, first visit before install)
				dispatch(markQueueUnavailable());
			});

		return () => {
			cancelled = true;
			if (handshakeTimer) clearTimeout(handshakeTimer);
			navigator.serviceWorker.removeEventListener("message", handleMessage);
		};
	}, [getDrainScheduler]);

	return <NetworkStatusContext.Provider value={{ isOnline }}>{children}</NetworkStatusContext.Provider>;
};

export default NetworkStatusProvider;
